import {
  IonButton,
  IonInput,
  IonItem,
  IonSpinner,
  IonText,
} from '@ionic/react';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCrateState,
  updateTrackBPM,
} from '../../store/slices/crate.slice';
import { AppDispatch } from '../../store/store';
import { useToast } from '../ToastManager/useToast';

interface EditableBPMFieldProps {
  trackId: string;
  bpmVal: number | undefined;
  className?: string;
  textColor?: string;
}

export const EditableBPMField: React.FC<EditableBPMFieldProps> = ({
  trackId,
  bpmVal,
  className = '',
  textColor = 'dark',
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isEditable } = useSelector(selectCrateState);
  const [displayValue, setDisplayValue] = useState<string>(
    bpmVal != null ? Math.round(bpmVal).toString() : '',
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const inputRef = useRef<HTMLIonInputElement>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const { errorToast } = useToast();

  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(bpmVal != null ? Math.round(bpmVal).toString() : '');
      setHasUnsavedChanges(false);
    }
  }, [bpmVal, isFocused]);

  const saveChanges = async () => {
    if (!hasUnsavedChanges) return;

    let bpmValue: number | undefined;

    if (displayValue.trim() === '') {
      bpmValue = undefined;
    } else {
      const numValue = parseFloat(displayValue);
      if (isNaN(numValue) || numValue <= 0 || numValue > 400) {
        errorToast({
          message:
            'BPM must be a positive number and less than or equal to 400',
        });
        setDisplayValue(bpmVal != null ? Math.round(bpmVal).toString() : '');
        setHasUnsavedChanges(false);
        return;
      }
      bpmValue = numValue;
    }
    setIsUpdating(true);
    try {
      const result = await dispatch(updateTrackBPM({ trackId, bpmValue }));

      if (updateTrackBPM.fulfilled.match(result)) {
        setHasUnsavedChanges(false);
      } else {
        setDisplayValue(bpmVal != null ? Math.round(bpmVal).toString() : '');
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      console.error('Error updating BPM:', error);
      setDisplayValue(bpmVal != null ? Math.round(bpmVal).toString() : '');
      setHasUnsavedChanges(false);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleInput = (e: CustomEvent<{ value: string | null }>) => {
    const newValue = e.detail.value ?? '';
    setDisplayValue(newValue);

    const originalValue = bpmVal?.toString() || '';
    if (newValue !== originalValue) {
      setHasUnsavedChanges(true);
    } else {
      setHasUnsavedChanges(false);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = async () => {
    setIsFocused(false);
    await saveChanges();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      saveChanges();
      inputRef.current?.blur();
    }
    if (e.key === 'Escape') {
      setDisplayValue(bpmVal != null ? Math.round(bpmVal).toString() : '');
      setHasUnsavedChanges(false);
      inputRef.current?.blur();
    }
  };

  const handleDouble = async () => {
    if (isUpdating) return;
    const currentBPM = bpmVal;
    if (currentBPM === undefined || isNaN(currentBPM)) return;

    const newBPM = Number((currentBPM * 2).toFixed(2));
    if (newBPM > 400) {
      errorToast({ message: 'BPM cannot exceed 400' });
      return;
    }

    setIsUpdating(true);
    setDisplayValue(Math.round(newBPM).toString());

    try {
      const result = await dispatch(
        updateTrackBPM({ trackId, bpmValue: newBPM }),
      );
      if (updateTrackBPM.fulfilled.match(result)) {
        setHasUnsavedChanges(false);
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const handleHalve = async () => {
    if (isUpdating) return;

    const currentBPM = bpmVal;

    if (currentBPM === undefined || isNaN(currentBPM)) return;

    if (currentBPM <= 0.01) {
      errorToast({ message: 'BPM must be greater than 0' });
      return;
    }

    const newBPM = Number((currentBPM / 2).toFixed(2));

    setIsUpdating(true);
    setDisplayValue(Math.round(newBPM).toString());

    try {
      const result = await dispatch(
        updateTrackBPM({ trackId, bpmValue: newBPM }),
      );
      if (updateTrackBPM.fulfilled.match(result)) {
        setHasUnsavedChanges(false);
      }
    } finally {
      setIsUpdating(false);
    }
  };

  if (!isEditable) {
    return (
      <IonText color={textColor}>
        <p style={{ margin: 0, textAlign: 'right', whiteSpace: 'nowrap' }}>
          {bpmVal != null ? Math.round(bpmVal) : '--'}
        </p>
      </IonText>
    );
  }
  return (
    <IonItem
      lines="none"
      className={className}
      style={{
        position: 'relative',
        '--padding-start': '0',
        '--padding-end': '0',
        '--inner-padding-start': '0',
        '--inner-padding-end': '0',
        '--background': 'transparent',
      }}
    >
      {bpmVal !== undefined && (
        <IonButton
          fill="clear"
          slot="start"
          onClick={handleHalve}
          title="Halve BPM"
          style={{
            margin: '0 2px 0 0',
            '--padding-start': '4px',
            '--padding-end': '4px',
          }}
          disabled={isUpdating}
        >
          &divide;2
        </IonButton>
      )}
      <div style={{ position: 'relative', flex: 1 }}>
        <IonInput
          ref={inputRef}
          type="text"
          inputmode="decimal"
          value={displayValue}
          onIonInput={(e) =>
            handleInput(e as CustomEvent<{ value: string | null }>)
          }
          onIonFocus={handleFocus}
          onIonBlur={handleBlur}
          onKeyDown={handleKeyDown}
          color={textColor}
          disabled={isUpdating}
          style={{
            textAlign: 'center',
            minWidth: '6ch',
            MozAppearance: 'textfield',
            WebkitAppearance: 'none',
            border: hasUnsavedChanges
              ? '2px solid var(--ion-color-primary)'
              : '1px solid transparent',
            borderRadius: '4px',
            opacity: isUpdating ? 0.6 : 1,
          }}
        />
        {isUpdating && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              right: '50px',
              transform: 'translateY(-50%)',
            }}
          >
            <IonSpinner
              name="crescent"
              style={{ width: '16px', height: '16px' }}
            />
          </div>
        )}
      </div>
      {bpmVal !== undefined && (
        <IonButton
          fill="clear"
          slot="end"
          onClick={handleDouble}
          title="Double BPM"
          style={{
            margin: '0 0 0 2px',
            '--padding-start': '4px',
            '--padding-end': '4px',
          }}
          disabled={isUpdating}
        >
          &times;2
        </IonButton>
      )}
    </IonItem>
  );
};
